import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title1,
  Title3,
  Text,
  Spinner,
  Badge,
  Button,
} from '@fluentui/react-components';
import { Link as RouterLink } from 'react-router-dom';
import { apiGet, apiPost } from '../api/client';
import type { MetricsSummary, DashboardSummary } from '../api/types';
import type { MatrixRunResponse, MatrixListItem } from '../api/matrix';
import { listMatrixRuns } from '../api/matrix';
import {
  listBestModelByRole, type BestModelByRoleItem,
  listPendingAbReview, type PendingAbReviewItem,
  getSimulationDashboard, type SimulationDashboard,
} from '../api/dashboard';
import { providerBadgeColor, RETIRED_PROVIDER_IDS as RETIRED_IDS } from '../lib/chatModelId';
import type { ExecSeriesPoint } from '../components/execTypes';
import { MetricCard } from '../components/MetricCard';
import { ExecutiveRecommendation } from '../components/ExecutiveRecommendation';
import { ScoreBarChart } from '../components/ScoreBarChart';

const useStyles = makeStyles({
  header: { marginBottom: '24px' },
  section: { marginTop: '32px', marginBottom: '16px' },
  grid: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginBottom: '32px',
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  td: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    color: tokens.colorNeutralForeground1,
  },
  tdRight: {
    padding: '10px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: '12px 0',
  },
  sectionHint: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
    display: 'block',
  },
  fullRunCard: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  fullRunHint: {
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
    display: 'block',
  },
  fullRunResult: {
    marginBottom: '32px',
    display: 'grid',
    gap: '16px',
  },
  retiredRow: {
    color: tokens.colorNeutralForeground3,
  },
  bestGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '12px',
    padding: '16px',
  },
  bestCard: {
    padding: '12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  matrixLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    fontVariantNumeric: 'tabular-nums',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px',
  },
});

const FULL_RUN_LOADING_MESSAGES = [
  'Picking default dataset & prompt…',
  'Fanning out across configured providers…',
  'Scoring answers against expected keywords…',
  'Ranking prompt × provider cells…',
  'Building executive recommendation…',
];

// Providers that have been retired from the benchmark fan-out but whose
// historical rows still surface in the scorecard. Rendered de-emphasized with
// a "Retired" badge so viewers don't compare stale data head-to-head with
// current-fleet providers.
// M6 — retired provider ids centralized in lib/chatModelId. Aliased above.
const RETIRED_PROVIDER_IDS = RETIRED_IDS;

// Null → em-dash. All-error rows in the scorecard now return null for score,
// cost, latency, and winRate — see api/types.ts ProviderScorecard. Rendering
// null as "0.000" would be indistinguishable from a legitimate zero result.
function pct(x: number | null): string { return x === null ? '—' : `${(x * 100).toFixed(1)}%`; }
function money(x: number | null): string { return x === null ? '—' : `$${x.toFixed(4)}`; }
function moneyFine(x: number | null): string { return x === null ? '—' : `$${x.toFixed(6)}`; }
function mb(bytes: number): string { return `${(bytes / (1024 * 1024)).toFixed(1)} MB`; }
function score(x: number | null): string { return x === null ? '—' : x.toFixed(3); }
function latencyMs(x: number | null): string { return x === null ? '—' : `${x.toFixed(0)} ms`; }
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function Dashboard() {
  const s = useStyles();
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [dash, setDash] = useState<DashboardSummary | null>(null);
  const [matrixRuns, setMatrixRuns] = useState<MatrixListItem[] | null>(null);
  const [bestByRole, setBestByRole] = useState<BestModelByRoleItem[] | null>(null);
  const [pendingAb, setPendingAb] = useState<PendingAbReviewItem[] | null>(null);
  const [simDash, setSimDash] = useState<SimulationDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Run Full Benchmark (M6) — one-click flow.
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<MatrixRunResponse | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);

  // Rotate contextual messages every 3s while a run is in flight (per global rule).
  useEffect(() => {
    if (!running) return;
    const id = setInterval(
      () => setLoadingMsgIdx((i) => (i + 1) % FULL_RUN_LOADING_MESSAGES.length),
      3000,
    );
    return () => clearInterval(id);
  }, [running]);

  const runFullPoints = useMemo<ExecSeriesPoint[]>(() =>
    runResult?.aggregates.map((a) => ({
      key: `${a.promptId}|${a.providerId}`,
      label: `${a.promptName} × ${a.providerName}`,
      overallScore: a.avgOverallScore,
      costUsd: a.totalCostUsd,
      latencyMs: a.avgLatencyMs,
      hallucinationRisk: a.avgHallucinationRisk,
      isError: a.errorCount > 0 && a.errorCount === a.runCount,
      errorText: a.errorCount > 0 ? `${a.errorCount}/${a.runCount} errored` : undefined,
    })) ?? [],
  [runResult]);

  async function runFullBenchmark() {
    setRunning(true);
    setRunErr(null);
    setRunResult(null);
    setLoadingMsgIdx(0);
    try {
      const r = await apiPost<MatrixRunResponse>('/api/benchmarks/run-full', {});
      setRunResult(r);
      // A full run writes new requests + evaluations, so the top-line KPI cards
      // and the provider scorecard are now stale. Re-fetch metrics + dashboard
      // summary alongside the matrix runs so the whole page reflects the run
      // without a manual reload.
      try {
        const [m, d, list] = await Promise.all([
          apiGet<MetricsSummary>('/api/metrics/summary'),
          apiGet<DashboardSummary>('/api/dashboard/summary'),
          listMatrixRuns(5),
        ]);
        setMetrics(m); setDash(d); setMatrixRuns(list);
      } catch { /* non-fatal — cards keep last-known values */ }
    } catch (e) {
      const msg = (e as Error).message;
      const match = msg.match(/^API \d+: (.+)$/s);
      setRunErr(match ? match[1] : msg);
    } finally {
      setRunning(false);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Fire in parallel — independent aggregates.
        const [m, d, mr, best, pending, sim] = await Promise.all([
          apiGet<MetricsSummary>('/api/metrics/summary'),
          apiGet<DashboardSummary>('/api/dashboard/summary'),
          listMatrixRuns(5).catch(() => [] as MatrixListItem[]),
          listBestModelByRole(30).catch(() => [] as BestModelByRoleItem[]),
          listPendingAbReview().catch(() => [] as PendingAbReviewItem[]),
          getSimulationDashboard(30).catch(() => null),
        ]);
        if (mounted) { setMetrics(m); setDash(d); setMatrixRuns(mr); setBestByRole(best); setPendingAb(pending); setSimDash(sim); }
      } catch (e) {
        if (mounted) setErr((e as Error).message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div>
      <div className={s.header}>
        <Title1 as="h1">Executive Dashboard</Title1>
        <Text>Aggregate signal across every request the platform has served.</Text>
      </div>

      {/* -------- M6: one-click Run Full Benchmark -------- */}
      <div className={s.fullRunCard}>
        <div>
          <Text weight="semibold" size={400}>Run Full Benchmark</Text>
          <Text className={s.fullRunHint} size={200}>
            Kicks the default golden dataset through every configured provider using the resolved production prompt. Powers a live executive snapshot in under a minute.
          </Text>
        </div>
        <Button appearance="primary" onClick={runFullBenchmark} disabled={running}>
          {running ? <Spinner size="tiny" label={FULL_RUN_LOADING_MESSAGES[loadingMsgIdx]} /> : 'Run Full Benchmark'}
        </Button>
      </div>

      {runErr && (
        <Text className={s.err} block style={{ marginBottom: 16 }}>
          Run Full Benchmark failed: {runErr}
        </Text>
      )}

      {runResult && (
        <div className={s.fullRunResult}>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            Dataset <b>{runResult.datasetName}</b> · {runResult.questionCount} questions ·
            {' '}{runResult.providerCount} providers · run {runResult.runId.slice(0, 8)}
          </Text>
          <ExecutiveRecommendation
            points={runFullPoints}
            serverRecommendation={runResult.recommendation}
            entityLabel="Prompt × Provider"
          />
          <ScoreBarChart points={runFullPoints} title="Overall score by provider" />
        </div>
      )}

      {loading && <Spinner label="Loading metrics…" />}
      {err && <Text className={s.err}>Failed to load metrics: {err}</Text>}

      {metrics && (
        <div className={s.grid}>
          <MetricCard label="Total requests" value={String(metrics.totalRequests)} hint="Requests scored end-to-end (all time)" />
          <MetricCard label="Avg quality score" value={metrics.averageQualityScore.toFixed(3)} hint="0.0 – 1.0 (weighted)" />
          <MetricCard label="Avg cost / call" value={money(metrics.totalRequests === 0 ? 0 : metrics.totalCostUsd / metrics.totalRequests)} />
          <MetricCard label="Avg latency" value={`${metrics.averageLatencyMs.toFixed(0)} ms`} />
          <MetricCard label="Injection pass rate" value={pct(metrics.promptInjectionPassRate)} hint="Fraction of red-team attacks resisted" />
          <MetricCard label="Failed evaluations" value={String(metrics.failedEvaluationsCount)} hint="Aggregate score < 0.5" />
          <MetricCard label="Total input tokens" value={metrics.totalInputTokens.toLocaleString()} />
          <MetricCard label="Total output tokens" value={metrics.totalOutputTokens.toLocaleString()} />
        </div>
      )}

      {dash && (
        <>
          {/* ------------------- Corpus ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Knowledge Corpus</Title3>
            <Text className={s.sectionHint} size={200}>
              Documents ingested, chunked, and available for retrieval.
            </Text>
          </div>
          <div className={s.grid}>
            <MetricCard label="Documents" value={String(dash.corpus.documentCount)} />
            <MetricCard label="Chunks" value={dash.corpus.chunkCount.toLocaleString()} hint="Embedded segments in the index" />
            <MetricCard label="Corpus size" value={mb(dash.corpus.totalSizeBytes)} />
            <MetricCard label="Last ingestion" value={fmtDate(dash.corpus.lastIngestionAt)} />
          </div>

          {/* ------------------- Model Scorecard ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Model Scorecard</Title3>
            <Text className={s.sectionHint} size={200}>
              Aggregate performance across benchmark runs in the last 30 days
              ({dash.benchmarkRunCountLast30Days} run{dash.benchmarkRunCountLast30Days === 1 ? '' : 's'}) —
              grouped by (provider, model) since Phase 4 so multi-model providers get their
              own rows. <strong>Runs</strong> counts every attempt including errors;
              <strong> Score / Safety / Latency / Cost / Win %</strong> average successes only.
              A row with all errors shows <strong>—</strong> for those columns. Retired providers are
              de-emphasized — historical rows only.
            </Text>
          </div>
          <div className={s.card}>
            {dash.providerScorecard.length === 0 ? (
              <div className={s.empty}>
                No benchmark data yet. Run a comparison from the Benchmark Lab to populate this view.
              </div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Provider</th>
                    <th className={s.th}>Model</th>
                    <th className={s.th}>Runs</th>
                    <th className={s.th}>Avg Quality</th>
                    <th className={s.th}>Avg Safety</th>
                    <th className={s.th}>Avg Latency</th>
                    <th className={s.th}>Avg Cost</th>
                    <th className={s.th}>Win Rate</th>
                    <th className={s.th}>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {dash.providerScorecard.map((p) => {
                    const retired = RETIRED_PROVIDER_IDS.has(p.providerId);
                    // M6 — key now composite. Backend groups by (providerId, modelName)
                    // so multiple rows can share providerId.
                    const rowKey = `${p.providerId}:${p.modelName}`;
                    return (
                      <tr key={rowKey} className={retired ? s.retiredRow : undefined}>
                        <td className={s.td}>
                          <Badge color={providerBadgeColor(p.providerId)} appearance="tint" size="small">
                            {p.providerName}
                          </Badge>
                          {retired && (
                            <Badge appearance="outline" color="subtle" style={{ marginLeft: 8 }}>
                              Retired
                            </Badge>
                          )}
                        </td>
                        <td className={s.td}><Text size={200}>{p.modelName}</Text></td>
                        <td className={s.tdRight}>{p.runCount}</td>
                        <td className={s.tdRight}>{score(p.averageOverallScore)}</td>
                        <td className={s.tdRight}>{score(p.averageSafetyScore)}</td>
                        <td className={s.tdRight}>{latencyMs(p.averageLatencyMs)}</td>
                        <td className={s.tdRight}>{moneyFine(p.averageCostUsd)}</td>
                        <td className={s.tdRight}>{pct(p.winRate)}</td>
                        <td className={s.tdRight}>
                          {p.errorCount === 0
                            ? <Badge appearance="tint" color="success">0</Badge>
                            : <Badge appearance="tint" color="danger">{p.errorCount}</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ------------------- Persona Simulation (Phase 7 M5) — compact summary ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Persona Simulation</Title3>
            <Text className={s.sectionHint} size={200}>
              Latest persona-simulation run at a glance. Full 30-day aggregates, the
              executive summary, and browsable run history live on the dedicated page.
            </Text>
          </div>
          <div className={s.fullRunCard}>
            {simDash === null ? (
              <Text className={s.empty}>Loading latest simulation…</Text>
            ) : !simDash.hasData || !simDash.latestRun ? (
              <>
                <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                  No simulations yet.
                </Text>
                <RouterLink to="/simulation-results" className={s.matrixLink}>
                  View persona results →
                </RouterLink>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Text weight="semibold">{simDash.latestRun.promptName}</Text>
                  <Badge appearance="tint">{simDash.latestRun.role}</Badge>
                  <Badge
                    appearance="tint"
                    color={simDash.latestRun.status === 'Complete' ? 'success'
                      : simDash.latestRun.status === 'Failed' ? 'danger' : 'warning'}
                  >
                    {simDash.latestRun.status}
                  </Badge>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
                    <Badge appearance="tint" color="success">{simDash.latestRun.passed} pass</Badge>
                    <Badge appearance="tint" color="danger">{simDash.latestRun.failed} fail</Badge>
                    <Badge appearance="tint" color="brand">{simDash.latestRun.blocked} blocked</Badge>
                    <Badge appearance="tint" color="brand">{simDash.latestRun.refusedCorrectly} refused</Badge>
                    {simDash.latestRun.errored > 0 && (
                      <Badge appearance="tint" color="warning">{simDash.latestRun.errored} err</Badge>
                    )}
                  </div>
                </div>
                <RouterLink to="/simulation-results" className={s.matrixLink}>
                  View persona results →
                </RouterLink>
              </>
            )}
          </div>

          {/* ------------------- Best Model by Role (M6) ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Best Model by Role — 30d rolling</Title3>
            <Text className={s.sectionHint} size={200}>
              For each SignalCare role, the chat model that won the most prompts across
              matrix runs in the last 30 days. Ties broken by mean winning score. Needs
              ≥3 successful samples per (prompt, model) to count a win.
            </Text>
          </div>
          <div className={s.card}>
            {bestByRole === null ? (
              <div className={s.empty}>Loading…</div>
            ) : bestByRole.length === 0 ? (
              <div className={s.empty}>No role data yet — run some matrix benchmarks.</div>
            ) : (
              <div className={s.bestGrid}>
                {bestByRole.map(b => (
                  <div key={b.role} className={s.bestCard}>
                    <Text weight="semibold">{b.role}</Text>
                    {b.status === 'ok' && b.winningChatModelId ? (
                      <>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Badge
                            color={providerBadgeColor(b.winningChatModelId.split(':')[0])}
                            appearance="tint"
                          >
                            {b.providerName} · {b.modelName}
                          </Badge>
                          {b.avgWinningScore !== null && (
                            <Badge>{pct(b.avgWinningScore)}</Badge>
                          )}
                        </div>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          Won {b.promptsWon} / {b.promptsInRole} prompts
                        </Text>
                      </>
                    ) : (
                      <Badge appearance="tint">No data</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ------------------- Prompts pending A/B review (M3) ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Prompts pending A/B review</Title3>
            <Text className={s.sectionHint} size={200}>
              Active prompts whose latest Draft has never been A/B tested against Production.
              Open the prompt to run the comparison and, if the draft wins, promote it in place.
            </Text>
          </div>
          <div className={s.card}>
            {pendingAb === null ? (
              <div className={s.empty}>Loading…</div>
            ) : pendingAb.length === 0 ? (
              <div className={s.empty}>Nothing pending — every Active prompt's latest Draft has been A/B'd.</div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Prompt</th>
                    <th className={s.th}>Role</th>
                    <th className={s.th}>Production</th>
                    <th className={s.th}>Untested Draft</th>
                    <th className={s.th}>Draft created</th>
                    <th className={s.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingAb.map((p) => (
                    <tr key={p.promptId}>
                      <td className={s.td}>{p.promptName}</td>
                      <td className={s.td}>
                        <Badge appearance="tint">{p.role}</Badge>
                      </td>
                      <td className={s.td}>v{p.productionVersionNumber}</td>
                      <td className={s.td}>
                        <Badge appearance="outline">v{p.latestDraftVersionNumber}</Badge>
                      </td>
                      <td className={s.td}>{new Date(p.draftCreatedAt).toLocaleString()}</td>
                      <td className={s.td}>
                        <RouterLink to={`/prompts?prompt=${p.promptId}&tab=abtest`} className={s.matrixLink}>
                          Compare →
                        </RouterLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ------------------- Latest Matrix Runs ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Latest Matrix Runs</Title3>
            <Text className={s.sectionHint} size={200}>
              Most recent prompt × provider matrix runs — each covers a full golden dataset.
              Winner shown as the top-ranked cell key.
            </Text>
          </div>
          <div className={s.card}>
            {matrixRuns === null ? (
              <div className={s.empty}>Loading matrix runs…</div>
            ) : matrixRuns.length === 0 ? (
              <div className={s.empty}>
                No matrix runs yet. Trigger one from the Matrix Lab or click Run Full Benchmark above.
              </div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Dataset</th>
                    <th className={s.th}>Role</th>
                    <th className={s.th}>Cells</th>
                    <th className={s.th}>Winner Score</th>
                    <th className={s.th}>When</th>
                    <th className={s.th}>Run</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixRuns.map((r) => (
                    <tr key={r.runId}>
                      <td className={s.td}>{r.datasetName}</td>
                      <td className={s.td}><Text size={200}>{r.role}</Text></td>
                      <td className={s.tdRight}>
                        {r.questionCount} × {r.promptCount} × {r.chatModelCount || r.providerCount}
                      </td>
                      <td className={s.tdRight}>{score(r.winningScore)}</td>
                      <td className={s.td}><Text size={200}>{fmtDate(r.createdAt)}</Text></td>
                      <td className={s.td}>
                        <RouterLink to={`/matrix?run=${r.runId}`} className={s.matrixLink}>
                          {r.runId.slice(0, 8)}
                        </RouterLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ------------------- Recent Single-Question Benchmarks ------------------- */}
          <div className={s.section}>
            <Title3 as="h2">Recent Single-Question Benchmarks</Title3>
            <Text className={s.sectionHint} size={200}>
              Latest head-to-head comparisons from the Benchmark Lab — winner is the top-ranked
              provider by aggregate score. For dataset-wide runs, see Latest Matrix Runs above.
            </Text>
          </div>
          <div className={s.card}>
            {dash.recentBenchmarks.length === 0 ? (
              <div className={s.empty}>
                No benchmark runs yet.
              </div>
            ) : (
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Question</th>
                    <th className={s.th}>Providers</th>
                    <th className={s.th}>Winner</th>
                    <th className={s.th}>Score</th>
                    <th className={s.th}>When</th>
                  </tr>
                </thead>
                <tbody>
                  {dash.recentBenchmarks.map((r) => (
                    <tr key={r.runId}>
                      <td className={s.td}>{r.question}</td>
                      <td className={s.tdRight}>{r.providerCount}</td>
                      <td className={s.td}>
                        {r.winningProviderName
                          ? <Badge appearance="tint" color="brand">{r.winningProviderName}</Badge>
                          : <Text size={200}>—</Text>}
                      </td>
                      <td className={s.tdRight}>{score(r.winningScore)}</td>
                      <td className={s.td}><Text size={200}>{fmtDate(r.createdAt)}</Text></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
